/**
 * Table Settings Mapper
 *
 * Bidirectional mappings between the frontend's column/tab/department identifiers
 * and the backend API's identifiers for SE Metrics table settings.
 *
 * Backend API schema (GET /users/user response → seMetricsTableSettings):
 * {
 *   "COMMERCIAL_AND_CONSUMER": {
 *     "ENGINEERING_PERFORMANCE_KPIS": ["MEAN_TIME_TO_RESOLVE", "APP_RESPONSE_TIME", ...],
 *     "RISK_AND_RELIABILITY": ["..."],
 *     "AI_PRODUCTIVITY": ["..."]
 *   },
 *   ...
 * }
 *
 * Frontend preference key format: `${tabId}__${departmentId}`
 * e.g. "engineering-performance__commercial-and-consumer"
 */

import type {TableColumnSettings} from '@/store/slices/tableSettingsSlice'

// ─── Department mapping ──────────────────────────────────────────────────────

const DEPARTMENT_TO_API: Record<string, string> = {
  'Commercial & Consumer': 'COMMERCIAL_AND_CONSUMER',
  'Supply Chain': 'SUPPLY_CHAIN',
  'IBP & Global Functions': 'IBP_AND_GLOBAL_FUNCTIONS',
  DSX: 'DSX',
}

const API_TO_DEPARTMENT: Record<string, string> = Object.fromEntries(
  Object.entries(DEPARTMENT_TO_API).map(([k, v]) => [v, k]),
)

export const departmentNameToApiKey = (name: string): string | undefined => DEPARTMENT_TO_API[name]
export const apiKeyToDepartmentName = (key: string): string | undefined => API_TO_DEPARTMENT[key]

// ─── Tab mapping ─────────────────────────────────────────────────────────────

const TAB_TO_API: Record<string, string> = {
  'engineering-performance': 'ENGINEERING_PERFORMANCE_KPIS',
  'risk-reliability-vulnerabilities': 'RISK_AND_RELIABILITY',
  'risk-reliability-incidents': 'INCIDENTS',
  'ai-metrics': 'AI_PRODUCTIVITY',
}

const API_TO_TAB: Record<string, string> = Object.fromEntries(Object.entries(TAB_TO_API).map(([k, v]) => [v, k]))

export const tabIdToApiKey = (tabId: string): string | undefined => TAB_TO_API[tabId]
export const apiKeyToTabId = (key: string): string | undefined => API_TO_TAB[key]

// ─── Column mapping (frontend columnKey ↔ API column ID) ────────────────────

// Engineering Performance KPIs
const EP_COLUMN_TO_API: Record<string, string> = {
  gtAdoptionRate: 'GT_ADOPTION_RATE',
  buildSuccessRate: 'BUILD_SUCCESS_RATE',
  deployFreq: 'DEPLOYMENT_FREQUENCY',
  automationCoverage: 'TEST_AUTOMATION',
  overallAutomationCoverage: 'OVERALL_TEST_AUTOMATION',
  unitCoverage: 'UNIT_TEST_COVERAGE',
  appResponse: 'APP_RESPONSE_TIME',
  reusability: 'REUSABILITY',
  leadTime: 'LEAD_TIME_TO_CHANGE',
  availability: 'AVAILABILITY',
  crashRate: 'APP_CRASH_RATE',
  changeFailRate: 'CHANGE_FAIL_RATE',
  meanTimeToResolve: 'MEAN_TIME_TO_RESOLVE',
}

// Risk & Reliability — Vulnerabilities
const RR_VULN_COLUMN_TO_API: Record<string, string> = {
  critical: 'APPSECFLOW_VULNERABILITY.Critical',
  high: 'APPSECFLOW_VULNERABILITY.High',
  medium: 'APPSECFLOW_VULNERABILITY.Medium',
  low: 'APPSECFLOW_VULNERABILITY.Low',
  informational: 'APPSECFLOW_VULNERABILITY.Informational',
  totalAging60d: 'APPSECFLOW_VULNERABILITY.Total',
}

// Risk & Reliability — Incidents
const RR_INC_COLUMN_TO_API: Record<string, string> = {
  totalIncidentsP1P3: 'INCIDENT_COUNTS.highPriorityOverallIncidents',
  codeFixIncidentsP1P3: 'INCIDENT_COUNTS.highPriorityCodeFixIncidents',
  totalIncidentsP4P5: 'INCIDENT_COUNTS.lowPriorityOverallIncidents',
  codeFixIncidentsP4P5: 'INCIDENT_COUNTS.lowPriorityCodeFixIncidents',
  leadTime: 'LEAD_TIME_TO_CHANGE',
  availability: 'AVAILABILITY',
  changeFailRate: 'CHANGE_FAIL_RATE',
  meanTimeToResolve: 'MEAN_TIME_TO_RESOLVE',
}

// AI Productivity
const AI_COLUMN_TO_API: Record<string, string> = {
  aiAdoption: 'AI_PRODUCTIVITY.aiAdoptionRate',
  productivityGains: 'AI_PRODUCTIVITY.productivityGainPercentage',
  spSaved: 'AI_PRODUCTIVITY.totalAiAssistedUserStoryPointSaving',
  totalEffort: 'AI_PRODUCTIVITY.totalUserStoryPoints',
  hoursSaved: 'AI_PRODUCTIVITY.hoursSaved',
  totalHours: 'AI_PRODUCTIVITY.totalHours',
}

// Combined map per tab
const COLUMN_MAPS: Record<string, Record<string, string>> = {
  ENGINEERING_PERFORMANCE_KPIS: EP_COLUMN_TO_API,
  RISK_AND_RELIABILITY: RR_VULN_COLUMN_TO_API,
  INCIDENTS: RR_INC_COLUMN_TO_API,
  AI_PRODUCTIVITY: AI_COLUMN_TO_API,
}

// Build reverse maps
const REVERSE_COLUMN_MAPS: Record<string, Record<string, string>> = {}
for (const [tabApiKey, fwdMap] of Object.entries(COLUMN_MAPS)) {
  REVERSE_COLUMN_MAPS[tabApiKey] = Object.fromEntries(Object.entries(fwdMap).map(([k, v]) => [v, k]))
}

/**
 * Convert a frontend columnKey to its API column ID for a given tab.
 */
export const columnKeyToApiId = (tabApiKey: string, columnKey: string): string | undefined =>
  COLUMN_MAPS[tabApiKey]?.[columnKey]

/**
 * Convert an API column ID to the frontend columnKey for a given tab.
 */
export const apiIdToColumnKey = (tabApiKey: string, apiId: string): string | undefined =>
  REVERSE_COLUMN_MAPS[tabApiKey]?.[apiId]

/**
 * Get all configurable column keys for a given tab (frontend side).
 */
export const getConfigurableColumnKeys = (tabApiKey: string): string[] => Object.keys(COLUMN_MAPS[tabApiKey] ?? {})

// ─── Preference key helpers ─────────────────────────────────────────────────

/**
 * Parse a frontend preferenceKey into its tab and department parts.
 * Format: `${tabId}__${departmentSlug}`
 */
export const parsePreferenceKey = (preferenceKey: string): {tabId: string; departmentSlug: string} | null => {
  const parts = preferenceKey.split('__')
  if (parts.length !== 2) return null
  return {tabId: parts[0], departmentSlug: parts[1]}
}

/**
 * Resolve a frontend preferenceKey to API department + tab keys.
 */
export const preferenceKeyToApiKeys = (preferenceKey: string): {deptApiKey: string; tabApiKey: string} | null => {
  const parsed = parsePreferenceKey(preferenceKey)
  if (!parsed) return null

  const tabApiKey = tabIdToApiKey(parsed.tabId)
  if (!tabApiKey) return null

  // departmentSlug → department name → API key
  // We need to match the slug back to a department name
  const deptApiKey = findDeptApiKeyFromSlug(parsed.departmentSlug)
  if (!deptApiKey) return null

  return {deptApiKey, tabApiKey}
}

/**
 * Match a department slug (from preferenceKey) to its API key.
 * Slugs are normalised department names (lowercase, spaces→hyphens, &→-).
 */
const slugifyDeptName = (name: string): string =>
  name.toLowerCase().replace(/&/g, '-').replace(/\s+/g, '-').replace(/-{2,}/g, '-')

const DEPT_SLUG_TO_API: Record<string, string> = Object.fromEntries(
  Object.entries(DEPARTMENT_TO_API).map(([name, apiKey]) => [slugifyDeptName(name), apiKey]),
)

const findDeptApiKeyFromSlug = (slug: string): string | undefined => DEPT_SLUG_TO_API[slug]

// ─── API response ↔ Redux state converters ──────────────────────────────────

/**
 * Convert the API's seMetricsTableSettings object into the frontend's Redux state.
 *
 * API format:
 *   { "COMMERCIAL_AND_CONSUMER": { "ENGINEERING_PERFORMANCE_KPIS": ["COL1", "COL2"] } }
 *
 * Redux format:
 *   { "engineering-performance__commercial-and-consumer": { columnVisibility: {...}, columnOrder: [...] } }
 *
 * The API array contains only VISIBLE columns in display order.
 * All configurable columns NOT in the array are hidden.
 */
export const apiResponseToReduxState = (
  apiSettings: Record<string, Record<string, string[]>>,
): Record<string, TableColumnSettings> => {
  const result: Record<string, TableColumnSettings> = {}

  for (const [deptApiKey, tabs] of Object.entries(apiSettings)) {
    const deptName = apiKeyToDepartmentName(deptApiKey)
    if (!deptName) continue
    const deptSlug = slugifyDeptName(deptName)

    for (const [tabApiKey, visibleApiIds] of Object.entries(tabs)) {
      const tabId = apiKeyToTabId(tabApiKey)
      if (!tabId) continue

      const allColumnKeys = getConfigurableColumnKeys(tabApiKey)
      const reverseMap = REVERSE_COLUMN_MAPS[tabApiKey] || {}

      // Build columnVisibility: true for columns in the API array, false otherwise
      const columnVisibility: Record<string, boolean> = {}
      for (const ck of allColumnKeys) {
        columnVisibility[ck] = false
      }

      // Build columnOrder from the API array (only visible columns)
      const columnOrder: string[] = []
      for (const apiId of visibleApiIds) {
        const ck = reverseMap[apiId]
        if (ck) {
          columnVisibility[ck] = true
          columnOrder.push(ck)
        }
      }

      const preferenceKey = `${tabId}__${deptSlug}`
      result[preferenceKey] = {
        columnVisibility,
        columnOrder,
        updatedAt: new Date().toISOString(),
      }
    }
  }

  return result
}

/**
 * Convert the frontend Redux state for a single table into the API POST payload.
 *
 * Returns the visible columns as API IDs in display order.
 */
export const reduxStateToApiPayload = (
  preferenceKey: string,
  columnVisibility: Record<string, boolean>,
  columnOrder: string[],
): {deptApiKey: string; tabApiKey: string; columns: string[]} | null => {
  const apiKeys = preferenceKeyToApiKeys(preferenceKey)
  if (!apiKeys) return null

  const fwdMap = COLUMN_MAPS[apiKeys.tabApiKey] || {}

  // Use columnOrder for ordering; include only visible columns
  const columns: string[] = []
  for (const ck of columnOrder) {
    if (columnVisibility[ck] !== false) {
      const apiId = fwdMap[ck]
      if (apiId) columns.push(apiId)
    }
  }

  // Also include any visible columns not in columnOrder (append at end)
  for (const [ck, visible] of Object.entries(columnVisibility)) {
    if (visible && !columnOrder.includes(ck)) {
      const apiId = fwdMap[ck]
      if (apiId) columns.push(apiId)
    }
  }

  return {
    deptApiKey: apiKeys.deptApiKey,
    tabApiKey: apiKeys.tabApiKey,
    columns,
  }
}
